import { z } from "zod/v4"
import { UserId, UserVrmRegistrationId } from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { CreatedAt, UpdatedAt } from "./system"
import { MediaImage } from "../common"

/**
 * VRM 登録の提供元（VRM ソース）を表す識別子。
 *
 * 値:
 * - "vc": VirtualCast。`/items/{itemId}` 経由で取得した VC の VRM。
 *
 * 将来別ソース（例: 自社配布、外部 VRM ホスティング）を追加する際は、ここに新しい値を追加する。
 * 連携解除・取得・削除はソース単位で行う（例: VC 連携解除時は `source === "vc"` のドキュメントだけが対象）。
 */
export const UserVrmRegistrationSourceVC = z.literal("vc")

export const UserVrmRegistrationSource = z.union([
  UserVrmRegistrationSourceVC,
])

export type UserVrmRegistrationSource = z.infer<typeof UserVrmRegistrationSource>

/**
 * VRM 0.x ライセンス情報。VRM ソースの API レスポンスから取得し、snake_case に変換して保存する。
 */
export const Vrm0License = z.object({
  allowed_user_name: z.enum(["OnlyAuthor", "ExplicitlyLicensedPerson", "Everyone"]).describe("アバターに人格を与えることの許諾範囲"),
  violent_usage_name: z.enum(["Disallow", "Allow"]).describe("暴力表現の許可"),
  sexual_usage_name: z.enum(["Disallow", "Allow"]).describe("性的表現の許可"),
  commercial_usage_name: z.enum(["Disallow", "Allow"]).describe("商用利用の許可"),
  other_permission_url: z.string().optional().describe("その他のライセンス条件 URL"),
  license_name: z.enum(["Redistribution_Prohibited", "CC0", "CC_BY", "CC_BY_NC", "CC_BY_SA", "CC_BY_NC_SA", "CC_BY_ND", "CC_BY_NC_ND", "Other"]).describe("ライセンスタイプ"),
  other_license_url: z.string().optional().describe("その他ライセンス条件 URL"),
})

export type Vrm0License = z.infer<typeof Vrm0License>

/**
 * VRM 1.x ライセンス情報。VRM ソースの API レスポンスから取得し、snake_case に変換して保存する。
 */
export const Vrm1License = z.object({
  avatar_permission: z.enum(["onlyAuthor", "onlySeparatelyLicensedPerson", "everyone"]).describe("人格付与の許諾範囲"),
  allow_excessively_violent_usage: z.boolean().describe("過剰な暴力表現の許可"),
  allow_excessively_sexual_usage: z.boolean().describe("過剰な性的表現の許可"),
  commercial_usage: z.enum(["personalNonProfit", "personalProfit", "corporation"]).describe("商用利用の許可範囲"),
  allow_political_or_religious_usage: z.boolean().describe("政治・宗教用途の許可"),
  allow_antisocial_or_hate_usage: z.boolean().describe("反社会的・憎悪表現の許可"),
  credit_notation: z.enum(["required", "unnecessary"]).describe("クレジット表記の要否"),
  allow_redistribution: z.boolean().describe("再配布の許可"),
  modification: z.enum(["prohibited", "allowModification", "allowModificationRedistribution"]).describe("改変の許可範囲"),
  license_url: z.string().describe("ライセンス文書 URL"),
})

export type Vrm1License = z.infer<typeof Vrm1License>

/**
 * VRM ライセンス情報の共用型。vrm_version に応じて 0.x / 1.x いずれかの形式が格納される。
 */
export const VrmLicense = z.union([Vrm0License, Vrm1License])

export type VrmLicense = z.infer<typeof VrmLicense>

export const VrmValidationStatusPending = z.literal("pending")
export const VrmValidationStatusPassed = z.literal("passed")
export const VrmValidationStatusFailed = z.literal("failed")
export const VrmValidationStatusError = z.literal("error")
export const VrmValidationStatusBanned = z.literal("banned")

export const VrmValidationStatus = z.union([
  VrmValidationStatusPending,
  VrmValidationStatusPassed,
  VrmValidationStatusFailed,
  VrmValidationStatusError,
  VrmValidationStatusBanned,
])

export type VrmValidationStatus = z.infer<typeof VrmValidationStatus>

export const VrmValidationErrorItem = z.object({
  item: z.string().describe("検証項目名 (例: polygon, material, mesh)"),
  count: z.number().describe("実測値"),
  limit: z.number().describe("上限値"),
})

export type VrmValidationErrorItem = z.infer<typeof VrmValidationErrorItem>

export const UserVrmRegistrationDocumentMeta = z.object({
  user_id: z.string().describe("ユーザーID（コレクショングループクエリ対応のため。パスのIDはクエリ条件に使用できない制約がある）"),
  source: UserVrmRegistrationSource.describe("VRM 提供元。'vc' = VirtualCast。将来他ソース追加時に拡張する。連携解除・列挙はこのフィールドを where 句で絞り込んで行う。"),
  item_id: z.string().describe("ソース側のアイテムID。source='vc' のときは VC の itemId。"),
  file_id: z.string().describe("TSO API 用の fileId。Unity 側が /files/{file_id} で VRM バイナリを取得する際に使用する。"),
  name: z.string().describe("VRM の表示名。登録時にソース API から取得した値を保持する。"),
  vc_icon: z.string().optional().describe("VRM 登録時に VC API から取得したアイコン画像 URL。STS 撮影完了前のフォールバック用。"),
  icon: z.string().optional().describe("VRM のサムネイル画像 URL（icon サイズ）。STS 撮影完了後に CDN URL が格納される。"),
  media: MediaImage.optional().describe("STS で撮影したプロフィール画像の CDN バリアント付き MediaImage。4089x2556 で撮影し複数解像度バリアントを持つ。"),
  soul_id: z.string().optional().describe("紐付けられた魂のID。souls コレクションのドキュメントIDを指す。未設定時はデフォルト挙動。"),
  vrm_validation_status: VrmValidationStatus.optional().describe("STS による VRM バリデーションのステータス。pending=検証中, passed=合格, failed=不合格, error=API通信エラー, banned=運営指定NG。未設定の場合は検証未実施（既存データ互換）。"),
  vrm_validation_errors: z.array(VrmValidationErrorItem).optional().describe("バリデーション失敗時に上限を超過した項目の一覧。status=failed の場合のみ設定される。"),
  vrm_validation_status_before_ban: VrmValidationStatus.optional().describe("運営指定NG設定前のバリデーションステータス。banned 解除時に元の状態に復元するために使用する。"),
  vrm_version: z.number().optional().describe("VRM バージョン。0 = VRM 0.x、1 = VRM 1.x。"),
  vrm_license: VrmLicense.optional().describe("VRM ライセンス情報。vrm_version=0 なら Vrm0License、vrm_version=1 なら Vrm1License が格納される。"),
  registered_at: z.number().describe("登録日時 (UNIX Timestamp ms)。クールダウン制御に使用する。"),
})

export type UserVrmRegistrationDocumentMeta = z.infer<typeof UserVrmRegistrationDocumentMeta>

export const UserVrmRegistrationDocumentBody = z.intersection(
  UserVrmRegistrationDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  }),
)

export type UserVrmRegistrationDocumentBody = z.infer<typeof UserVrmRegistrationDocumentBody>

export type UserVrmRegistrationDocument = UserVrmRegistrationDocumentBody & {
  id: UserVrmRegistrationId,
}

export class UserVrmRegistrationDocuments {

  static toDocumentData(doc: UserVrmRegistrationDocument): object {
    return {
      ...doc,
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): UserVrmRegistrationDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: UserVrmRegistrationId(doc.id),
      user_id: UserId(data["user_id"]),
      source: data["source"],
      item_id: data["item_id"],
      file_id: data["file_id"],
      name: data["name"],
      vc_icon: data["vc_icon"],
      icon: data["icon"],
      media: data["media"],
      soul_id: data["soul_id"],
      vrm_validation_status: data["vrm_validation_status"],
      vrm_validation_errors: data["vrm_validation_errors"],
      vrm_validation_status_before_ban: data["vrm_validation_status_before_ban"],
      vrm_version: data["vrm_version"],
      vrm_license: data["vrm_license"],
      registered_at: data["registered_at"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}

/**
 * `PUT /users/me/vrm-registrations` のリクエストボディ。
 *
 * VC の itemId を受け取り、Backend が VC `/items/{itemId}` を呼び出して検証した上で
 * `users/{userId}/vrm-registrations/{registrationId}` を作成する。
 */
export const RegisterVrmRegistrationRequest = z.object({
  itemId: z.string().min(1).describe("VC の itemId。VC `/user/items` 経由でクライアントが取得した値を指定する。"),
})

export type RegisterVrmRegistrationRequest = z.infer<typeof RegisterVrmRegistrationRequest>

/**
 * `PUT /users/me/vrm-registrations` のレスポンス。
 *
 * 作成された VRM 登録ドキュメントの ID を返す。クライアントはこの ID を用いて
 * `PUT /users/me/look`（VC variant）でルックを切り替える。
 */
export const RegisterVrmRegistrationResponse = z.object({
  vrmRegistrationId: z.string().min(1).describe("作成された VRM 登録ドキュメントの ID。`users/{userId}/vrm-registrations/{registrationId}` を指す。"),
})

export type RegisterVrmRegistrationResponse = z.infer<typeof RegisterVrmRegistrationResponse>

/**
 * `PUT /users/me/vrm-registrations/{id}/soul` のリクエストボディ。
 *
 * 登録済み VRM に魂を紐付ける。プレミアム会員のみ使用可能。
 * soul_id は `souls` コレクションに存在する有効な魂の ID を指定する。
 * null を指定すると魂の設定を解除する。
 */
export const UpdateVrmRegistrationSoulRequest = z.object({
  soulId: z.string().min(1).nullable().describe("設定する魂の ID。null で解除。"),
})

export type UpdateVrmRegistrationSoulRequest = z.infer<typeof UpdateVrmRegistrationSoulRequest>
